import { EMPTY_OBJECT, invariant } from "@multiweb/reactUtils";
import { ReduxCompatibleValue, TypedObject } from "@multiweb/types/react";
import { ControlReduxRawValue } from "../types";
import {
    CustomizableInstantValidationFnKey,
    IInstantValidationFnDescription,
    InstantValidationFnKey,
    InstantValidationFunction,
} from "./types";
import {
    getTypedParamsForCharsDeletedValidOrFail,
    getTypedParamsForIsMaxLengthValidOrFail,
    getTypedParamsForIsNumberCharactersOnlyValidOrFail,
    getTypedParamsForIsNumbersOnlyValidOrFail,
} from "./utils";
import * as validationFunctions from "./validationFunctions";

/**
 * Tento soubor obsahuje validace spouštěné (interně value-reducerem) okamžitě při psaní na každý `onChange`.
 * Pokud validace neprojde, pak se nová hodnota do reduceru ani nezapíše.
 * Více viz `readme.md`.
 */

/**
 * Zvaliduje, zda zadaná řetězcová hodnota není delší, než maximální délka.
 *
 * @param value Hodnota k validaci. Musí to být řetězec.
 * @param args Obsahuje maximální délku, vůči níž se `value` validuje.
 * Spolu s `value` musí vyhovovat rozhraní {@link IValidateMaxLengthParams}.
 * @return `true`, pokud validace projde.
 */
export const isMaxLengthValid: InstantValidationFunction = (value, args: TypedObject<ReduxCompatibleValue>) => {
    const typedParams = getTypedParamsForIsMaxLengthValidOrFail(value, args);
    return validationFunctions.isMaxLengthValid(typedParams);
};

/**
 * Zvaliduje, zda zadaná řetězcová hodnota obsahuje pouze čísla.
 *
 * @param value Hodnota k validaci. Musí to být řetězec.
 * @param args Musí být prázdné.
 * Spolu s `value` musí vyhovovat rozhraní {@link IIsNumbersOnlyValidParams}.
 * @return `true`, pokud validace projde.
 */
export const isNumbersOnlyValid: InstantValidationFunction = (value, args: TypedObject<ReduxCompatibleValue>) => {
    const typedParams = getTypedParamsForIsNumbersOnlyValidOrFail(value, args);
    return validationFunctions.isNumbersOnlyValid(typedParams);
};

/**
 * Zvaliduje, zda zadaná řetězcová hodnota není delší, než maximální délka a pokud je, tak validuje, jestli z hodnoty uživatel smazal nějaký znak.
 *
 * @param value Hodnota k validaci. Musí to být řetězec.
 * @param args Obsahuje maximální délku, vůči níž se `value` validuje a cílovou hodnotu, vůči které probíhá validace toho, zda byl při editaci smazán nějaký znak.
 * @return `true`, pokud validace projde.
 */
export const isLengthValid: InstantValidationFunction = (value, args: TypedObject<ReduxCompatibleValue>) => {
    const charsDeletedValidParams = getTypedParamsForCharsDeletedValidOrFail(value, args);
    const isMaxLengthValidParams = getTypedParamsForIsMaxLengthValidOrFail(value, args);

    const maxLengthValid = validationFunctions.isMaxLengthValid(isMaxLengthValidParams);

    return maxLengthValid ? maxLengthValid : validationFunctions.charsDeleted(charsDeletedValidParams);
};

/**
 * Kontroluje, zda zadaná řetězcová hodnota obsahuje pouze povolené znaky.
 * Je možné validovat pouze celá čísla.
 * @param value Řetězcová hodnota.
 * @param args Obsahuje příznak, zda se mají validovat pouze celá čísla.
 * @return `true`, pokud validace projde.
 */
export const isNumberCharactersOnlyValid: InstantValidationFunction = (value, args) => {
    const typedParams = getTypedParamsForIsNumberCharactersOnlyValidOrFail(value, args);
    return validationFunctions.isNumberCharactersOnlyValid(typedParams);
};

/**
 * Mapuje klíče na validační funkce. Určeno pro potřeby {@link isValueValid}.
 * Je možné sem doregistrovat další validační funkce přes {@link registerCustomValidationFunction}.
 */
export const validationFnMap = new Map<CustomizableInstantValidationFnKey, InstantValidationFunction>([
    [InstantValidationFnKey.maxLength, isMaxLengthValid],
    [InstantValidationFnKey.numbersOnly, isNumbersOnlyValid],
    [InstantValidationFnKey.length, isLengthValid],
    [InstantValidationFnKey.numberCharactersOnly, isNumberCharactersOnlyValid],
]);

/**
 * Vrací validační funkci se zadaným klíčem. Nebo vyhodí výjimku, pokud pro daný klíč funkce neexistuje.
 */
export const getValidationFn = (fnKey: CustomizableInstantValidationFnKey) => {
    const validationFn = validationFnMap.get(fnKey);
    invariant(!!validationFn, `[Controls/InstantValidation] Function for given key not found: ${fnKey}`);
    return validationFn;
};

/**
 * Zaregistruje zákaznickou validační funkci. Po zaregistrování bude možné tuto funkci předávat do {@link isValueValid}.
 * @param fnKey Klíč identifikující danou funkci. Musí být jedinečný, jinak se registrace nezdaří.
 * @param customFunction Validační funkce k zaregistrování.
 */
export const registerCustomValidationFunction = (fnKey: CustomizableInstantValidationFnKey, customFunction: InstantValidationFunction) => {
    if (validationFnMap.get(fnKey)) {
        throw new Error(`[Controls/InstantValidation] Function with given key already exists: ${fnKey}`);
    }
    validationFnMap.set(fnKey, customFunction);
};

/**
 * Zvaliduje zadanou hodnotu zadanými validačními funkcemi.
 * @param value Hodnota k validaci.
 * @param descriptions Seznam validací, které budou na `value` aplikovány.
 * @returns `true`, pokud všechny validace projdou. Jinak `false`.
 */
export const isValueValid = (value: ControlReduxRawValue, descriptions: ReadonlyArray<IInstantValidationFnDescription>): boolean => (
    descriptions.reduce((acc, currentDescription) => {
        const isValidFn = getValidationFn(currentDescription.fnKey);
        return acc && isValidFn(value, currentDescription.args);
    }, true as boolean)
);

/**
 * Převádí `maxLength` na instanci {IInstantValidationFnDescription} určenou pro {@link isMaxLengthValid}.
 */
export const createMaxLengthDescription = (maxLength: number): IInstantValidationFnDescription => ({
    fnKey: InstantValidationFnKey.maxLength,
    args: { maxLength },
});

/**
 * Vrací instanci {IInstantValidationFnDescription} určenou pro {@link isNumbersOnlyValid}.
 */
export const createNumbersOnlyDescription = (): IInstantValidationFnDescription => ({
    fnKey: InstantValidationFnKey.numbersOnly,
    args: EMPTY_OBJECT,
});

/**
 * Vrací instanci {IInstantValidationFnDescription} určenou pro {@link isLengthValid}.
 */
export const createLengthDescription = (targetValue: string, maxLength: number): IInstantValidationFnDescription => ({
    fnKey: InstantValidationFnKey.length,
    args: { targetValue, maxLength },
});

/**
 * Vrací instanci {IInstantValidationFnDescription} určenou pro {@link isNumberCharactersOnlyValid}.
 */
export const createNumberCharactersOnlyDescription = (isInteger: boolean): IInstantValidationFnDescription => ({
    fnKey: InstantValidationFnKey.numberCharactersOnly,
    args: { isInteger },
});

export const InstantValidation = {
    registerCustomValidationFunction,
    isValueValid,
    createMaxLengthDescription,
    createNumbersOnlyDescription,
    createLengthDescription,
    createNumberCharactersOnlyDescription,
};
